import React, { useEffect, useRef, useState, useCallback } from "react";
import { Lock, LogOut, Volume2, VolumeX, RefreshCw, Phone, Clock, Loader2, ShieldCheck, Zap, Ban } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sc_admin_token";

const beep = (ctxRef) => {
  try {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    // Loud 10-second alternating alarm so the kitchen never misses a ticket
    for (let i = 0; i < 20; i++) {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "square"; o.frequency.value = i % 2 === 0 ? 990 : 1320;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.5;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.4, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
      o.start(t); o.stop(t + 0.45);
    }
  } catch { /* ignore */ }
};

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await fetch(`${API}/admin/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pw }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Login failed");
      localStorage.setItem(TOKEN_KEY, d.token);
      onLogin();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-ink text-cream grid place-items-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-gold/20 bg-surface p-8 gold-glow" data-testid="admin-login">
        <div className="flex items-center gap-2 text-gold"><Lock size={18} /><span className="text-xs uppercase tracking-[0.24em] font-semibold">Kitchen Access</span></div>
        <h1 className="mt-3 font-display text-3xl font-semibold">Order Dashboard</h1>
        <p className="mt-1 text-sm text-cream/55">Enter the staff passcode to continue.</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Staff passcode" data-testid="admin-password"
          className="mt-5 w-full rounded-xl border border-white/12 bg-white/5 px-4 py-3 text-cream outline-none focus:border-gold/50" />
        {err && <p className="mt-2 text-sm text-red-300" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} data-testid="admin-login-btn" className="mt-4 w-full rounded-full bg-gold py-3.5 font-semibold text-ink disabled:opacity-60">{busy ? "Checking…" : "Enter Kitchen"}</button>
      </form>
    </div>
  );
};

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const Badge = ({ status, orderNumber }) => {
  if (status === "pay_at_pickup")
    return <span className="inline-block rounded-full bg-amber-400/90 px-2.5 py-1 text-[10px] font-bold uppercase text-ink" data-testid={`badge-pickup-${orderNumber}`}>Pay at pickup</span>;
  if (status === "paid")
    return <span className="inline-block rounded-full bg-emerald-500/90 px-2.5 py-1 text-[10px] font-bold uppercase text-ink" data-testid={`badge-paid-${orderNumber}`}>Paid online</span>;
  return <span className="inline-block rounded-full bg-red-500/90 px-2.5 py-1 text-[10px] font-bold uppercase text-white" data-testid={`badge-declined-${orderNumber}`}>Declined — do not prepare</span>;
};

const Ticket = ({ o, isNew, onRecheck, rechecking, onRefund, refundConfirm, refunding, refundMsg }) => {
  const refundedIdx = new Set((o.refunds || []).map((r) => r.item_index));
  const refundedTotal = (o.refunds || []).reduce((s, r) => s + parseFloat(r.amount), 0);
  return (
  <div className={`rounded-2xl border bg-surface p-5 transition-colors ${isNew ? "border-gold gold-glow" : o.status === "declined" ? "border-red-500/40" : "border-white/10"}`} data-testid={`ticket-${o.order_number}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-display text-xl font-semibold text-gold">{o.order_number}</p>
        <p className="text-xs text-cream/45">{fmtTime(o.paid_at || o.created_at)}</p>
      </div>
      {isNew && <span className="rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase text-ink animate-pulse">New</span>}
    </div>
    <div className="mt-2">
      <Badge status={o.status} orderNumber={o.order_number} />
    </div>
    <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5">
      {o.items.map((it, i) => {
        const refunded = refundedIdx.has(i);
        const key = `${o.id}:${i}`;
        return (
        <div key={i} className="flex items-center justify-between gap-2 text-sm">
          <span className={refunded ? "text-cream/35 line-through" : "text-cream"}>
            <span className="font-semibold text-gold">{it.qty}×</span> {it.name}{it.label !== "Reg" && it.label !== "Each" ? ` (${it.label})` : ""}
            {refunded && <span className="ml-2 rounded-full border border-red-500/30 bg-red-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-300" data-testid={`refunded-${o.order_number}-${i}`}>refunded</span>}
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="text-cream/50">${it.lineTotal}</span>
            {o.status === "paid" && !refunded && (
              <button onClick={() => onRefund(o, i)} disabled={!!refunding} data-testid={`refund-${o.order_number}-${i}`}
                title="Refund this item (price + tax)"
                className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase transition-colors ${refundConfirm === key ? "border-red-400 bg-red-500/20 text-red-200" : "border-white/15 text-cream/45 hover:border-red-400/50 hover:text-red-300"}`}>
                {refunding === key ? "…" : refundConfirm === key ? "Sure?" : "Refund"}
              </button>
            )}
          </span>
        </div>
        );
      })}
    </div>
    <div className="mt-3 border-t border-white/10 pt-3 text-sm space-y-1">
      <p className="font-semibold text-cream">{o.customer?.firstName} {o.customer?.lastName}</p>
      <p className="flex items-center gap-1.5 text-cream/60"><Phone size={13} /> {o.customer?.phone}</p>
      {o.customer?.pickupTime ? <p className="flex items-center gap-1.5 text-cream/60"><Clock size={13} /> Pickup: {o.customer.pickupTime}</p> : null}
      {o.customer?.notes ? <p className="text-cream/50 italic">“{o.customer.notes}”</p> : null}
    </div>
    <div className="mt-3 flex justify-between border-t border-white/10 pt-3">
      <span className="text-xs text-cream/45">Total (incl. tax {o.tip !== "0.00" ? "+ tip" : ""})</span>
      <span className="font-display text-lg font-semibold text-gold">${o.total}</span>
    </div>
    {refundedTotal > 0 && (
      <div className="mt-1.5 flex justify-between text-xs">
        <span className="text-red-300/70">Refunded so far</span>
        <span className="font-semibold text-red-300" data-testid={`refunded-total-${o.order_number}`}>-${refundedTotal.toFixed(2)}</span>
      </div>
    )}
    {o.status === "declined" && (
      <button onClick={() => onRecheck(o)} disabled={rechecking} data-testid={`recheck-${o.order_number}`}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-gold/40 bg-gold/10 px-3 py-2.5 text-xs font-semibold text-gold transition-colors hover:bg-gold/20 disabled:opacity-60">
        {rechecking ? <><Loader2 size={14} className="animate-spin" /> Re-checking…</> : <><ShieldCheck size={14} /> Re-check payment with Converge</>}
      </button>
    )}
    {o.status === "paid" && (
      <button onClick={() => onRefund(o, null)} disabled={!!refunding} data-testid={`refund-full-${o.order_number}`}
        className={`mt-3 w-full rounded-xl border px-3 py-2.5 text-xs font-semibold transition-colors disabled:opacity-60 ${refundConfirm === `${o.id}:full` ? "border-red-400 bg-red-500/20 text-red-200" : "border-white/15 text-cream/50 hover:border-red-400/50 hover:text-red-300"}`}>
        {refunding === `${o.id}:full` ? "Refunding…" : refundConfirm === `${o.id}:full` ? "Tap again to confirm FULL refund" : "Refund full payment"}
      </button>
    )}
    {refundMsg && refundMsg.id === o.id && (
      <p className={`mt-2 text-xs ${refundMsg.ok ? "text-emerald-400" : "text-red-300"}`} data-testid={`refund-msg-${o.order_number}`}>{refundMsg.text}</p>
    )}
  </div>
  );
};

export const AdminOrders = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [orders, setOrders] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [newIds, setNewIds] = useState(new Set());
  const [expired, setExpired] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rechecking, setRechecking] = useState(null);
  const [awake, setAwake] = useState(false);
  const [refundConfirm, setRefundConfirm] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [refundMsg, setRefundMsg] = useState(null);
  const [show86, setShow86] = useState(false);
  const [menu86, setMenu86] = useState(null);
  const seen = useRef(new Set());
  const first = useRef(true);
  const audioCtx = useRef(null);
  const wakeLock = useRef(null);
  const awakeRef = useRef(false);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  const load = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    try {
      const r = await fetch(`${API}/admin/orders?status=kitchen`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { setExpired(true); return; }
      const nt = r.headers.get("X-Refresh-Token");
      if (nt) localStorage.setItem(TOKEN_KEY, nt);
      const d = await r.json();
      const list = d.orders || [];
      const fresh = [];
      list.forEach((o) => { if (!seen.current.has(o.id)) fresh.push(o.id); });
      if (!first.current && fresh.length) {
        if (soundOn) beep(audioCtx);
        setNewIds((prev) => new Set([...fresh, ...prev]));
        setTimeout(() => setNewIds((prev) => { const n = new Set(prev); fresh.forEach((id) => n.delete(id)); return n; }), 20000);
      }
      list.forEach((o) => seen.current.add(o.id));
      first.current = false;
      setOrders(list);
      setExpired(false);
      setLastUpdated(new Date());
    } catch { /* keep previous */ }
  }, [soundOn]);

  useEffect(() => {
    if (!authed) return;
    first.current = true; seen.current = new Set();
    load();
    const id = setInterval(load, 5000);
    // Catch up instantly whenever the kitchen screen wakes or the tab regains focus
    const onVis = () => {
      if (document.visibilityState === "visible") {
        load();
        if (awakeRef.current && navigator.wakeLock) {
          navigator.wakeLock.request("screen").then((s) => { wakeLock.current = s; }).catch(() => {});
        }
      }
    };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [authed, load]);

  const toggleAwake = async () => {
    if (awake) {
      try { await wakeLock.current?.release(); } catch { /* ignore */ }
      wakeLock.current = null; awakeRef.current = false; setAwake(false);
      return;
    }
    try {
      wakeLock.current = await navigator.wakeLock.request("screen");
      awakeRef.current = true; setAwake(true);
    } catch { /* unsupported browser */ }
  };

  const recheck = async (o) => {
    setRechecking(o.id);
    try {
      await fetch(`${API}/admin/orders/reverify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ order_id: o.id }),
      });
    } catch { /* ignore */ }
    setRechecking(null);
    load();
  };

  const refund = async (o, idx) => {
    const key = `${o.id}:${idx === null ? "full" : idx}`;
    if (refundConfirm !== key) {
      setRefundConfirm(key);
      setTimeout(() => setRefundConfirm((c) => (c === key ? null : c)), 3500);
      return;
    }
    setRefundConfirm(null); setRefunding(key); setRefundMsg(null);
    try {
      const r = await fetch(`${API}/admin/orders/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ order_id: o.id, item_index: idx }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Refund failed");
      setRefundMsg({ id: o.id, ok: true, text: `Refunded $${d.refunded} — ${d.label}` });
    } catch (e) {
      setRefundMsg({ id: o.id, ok: false, text: e.message });
    }
    setRefunding(null);
    load();
  };

  const toggle86 = async () => {
    const next = !show86;
    setShow86(next);
    if (next && !menu86) {
      try {
        const r = await fetch(`${API}/menu`);
        const d = await r.json();
        setMenu86(d.items || []);
      } catch { /* ignore */ }
    }
  };

  const setSoldOut = async (itemId, val) => {
    setMenu86((m) => (m || []).map((it) => (it.id === itemId ? { ...it, soldOut: val } : it)));
    try {
      await fetch(`${API}/admin/availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ item_id: itemId, sold_out: val }),
      });
    } catch { /* ignore */ }
  };

  if (!authed) return <Login onLogin={() => { setAuthed(true); setExpired(false); }} />;

  const counts = {
    paid: orders.filter((o) => o.status === "paid").length,
    pickup: orders.filter((o) => o.status === "pay_at_pickup").length,
    declined: orders.filter((o) => o.status === "declined").length,
  };
  const cats86 = {};
  (menu86 || []).forEach((it) => { (cats86[it.category] = cats86[it.category] || []).push(it); });
  const soldCount = (menu86 || []).filter((i) => i.soldOut).length;

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="sticky top-0 z-10 glass border-b border-gold/15">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Live Kitchen</p>
            <h1 className="font-display text-2xl font-semibold">Order Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-cream/50" data-testid="live-indicator">
              <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
              Live{lastUpdated ? ` · updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
            </span>
            <button onClick={toggle86} data-testid="admin-86-toggle" title="Sold-out board" className="relative inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm font-semibold text-cream transition-colors hover:border-gold/40">
              <Ban size={16} /> <span className="hidden sm:inline">86 Board</span>
              {soldCount > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="86-count">{soldCount}</span>}
            </button>
            <button onClick={toggleAwake} data-testid="admin-awake-toggle" title={awake ? "Screen keep-awake ON" : "Keep screen awake"} className={`h-10 w-10 grid place-items-center rounded-full border transition-colors ${awake ? "border-gold bg-gold/15 text-gold" : "border-white/12 text-cream hover:border-gold/40"}`}><Zap size={18} /></button>
            <button onClick={() => setSoundOn((s) => !s)} data-testid="admin-sound-toggle" title="Toggle sound" className="h-10 w-10 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40">{soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
            <button onClick={load} title="Refresh" data-testid="admin-refresh" className="h-10 w-10 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40"><RefreshCw size={16} /></button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-2 text-sm font-semibold text-cream hover:border-gold/40"><LogOut size={16} /> Exit</button>
          </div>
        </div>
      </header>

      {expired && (
        <div className="bg-red-500/15 border-b border-red-500/30" data-testid="session-expired-banner">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-2.5 text-sm">
            <span className="text-red-300">Session expired — orders are not updating.</span>
            <button onClick={logout} data-testid="session-relogin" className="rounded-full bg-gold px-4 py-1.5 text-xs font-bold text-ink">Log in again</button>
          </div>
        </div>
      )}

      {show86 && (
        <div className="border-b border-white/10 bg-surface/60" data-testid="86-board">
          <div className="mx-auto max-w-7xl px-5 py-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">86 Board <span className="text-sm font-normal text-cream/50">— tap an item to mark it sold out; it disappears from the website instantly</span></h2>
              <button onClick={() => setShow86(false)} data-testid="86-close" className="text-sm text-cream/50 transition-colors hover:text-cream">Close</button>
            </div>
            {!menu86 ? (
              <p className="mt-4 text-sm text-cream/50">Loading menu…</p>
            ) : (
              <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(cats86).map(([cat, items]) => (
                  <div key={cat}>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-gold/70 font-semibold">{cat}</p>
                    <div className="mt-2 space-y-1.5">
                      {items.map((it) => (
                        <button key={it.id} onClick={() => setSoldOut(it.id, !it.soldOut)} data-testid={`86-${it.id}`}
                          className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors ${it.soldOut ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-white/10 bg-white/[0.03] text-cream/80 hover:border-gold/30"}`}>
                          <span className="truncate">{it.name}{it.label !== "Reg" && it.label !== "Each" ? ` · ${it.label}` : ""}</span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${it.soldOut ? "bg-red-500/80 text-white" : "bg-emerald-500/20 text-emerald-300"}`}>
                            {it.soldOut ? "86'd" : "In stock"}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-5 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-cream/50" data-testid="orders-summary">
          <span>{orders.length} order{orders.length !== 1 ? "s" : ""} · auto-refreshing every 5s</span>
          <span className="text-emerald-400 font-medium">{counts.paid} paid online</span>
          <span className="text-amber-300 font-medium">{counts.pickup} pay at pickup</span>
          <span className="text-red-400 font-medium">{counts.declined} declined</span>
        </div>
        {orders.length === 0 ? (
          <div className="grid place-items-center py-24 text-center text-cream/50">
            <p className="font-display text-2xl text-cream/70">No orders yet</p>
            <p className="mt-1 text-sm">Paid, pay-at-pickup and declined transactions appear here instantly with a chime.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {orders.map((o) => (
              <Ticket key={o.id} o={o} isNew={newIds.has(o.id)}
                onRecheck={recheck} rechecking={rechecking === o.id}
                onRefund={refund} refundConfirm={refundConfirm} refunding={refunding} refundMsg={refundMsg} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminOrders;
